import { MerchantValidationError } from "../error";
import * as Fields from "../fields";
import * as AddressFields from "../fields/address";
import { FieldSet } from "../fieldset";
import { Merchant } from "../merchant";

export type StoreLocationValueMap = {
  branchNumber: string;
  isMall: string;
  isResidence: string;
  label: string | undefined;
  locationCity: string;
  locationName: string;
  locationPostalCode: string;
  locationState: string;
  locationStreet: string;
  locationStreet2: string;
  mallName: string | undefined;
};

export type StoreLocationValueMapper<T> = {
  [K in keyof StoreLocationValueMap]: T;
};
interface StoreLocationOpts {
  locationIndex: number;
}
export class StoreLocation extends FieldSet<StoreLocationValueMap> {
  protected readonly _model: Merchant;
  static fromJson(
    Location: StoreLocation,
    {
      branchNumber,
      label,
      locationCity,
      locationName,
      locationPostalCode,
      locationState,
      locationStreet,
      locationStreet2,
      isMall,
      isResidence,
      mallName,
    }: StoreLocationValueMap
  ): StoreLocation {
    Location.branchNumber.value = branchNumber;
    Location.label.value = label;
    Location.locationCity.value = locationCity;
    Location.locationName.value = locationName;
    Location.locationPostalCode.value = locationPostalCode;
    Location.locationState.value = locationState;
    Location.locationStreet.value = locationStreet;
    Location.locationStreet2.value = locationStreet2;
    Location.isMall.value = isMall;
    Location.isResidence.value = isResidence;
    Location.mallName.value = mallName;

    return Location;
  }
  public locationIndex = 0;

  public branchNumber: Fields.BranchNumberField;
  public isMall: Fields.IsMallField;
  public isResidence: Fields.IsResidenceField;
  public label: Fields.LabelField;
  public locationCity: AddressFields.CityField;
  public locationName: Fields.LocationNameField;
  public locationPostalCode: AddressFields.PostalCodeField;
  public locationState: AddressFields.StateField;
  public locationStreet: AddressFields.StreetField;
  public locationStreet2: AddressFields.Street2Field;
  public mallName: Fields.MallNameField;

  constructor(model: Merchant, { locationIndex }: StoreLocationOpts) {
    super();

    this._model = model;

    this.locationIndex = locationIndex;
    this.fieldSetName = `Location${locationIndex}FieldSet`;

    this.branchNumber = new Fields.BranchNumberField(model);
    this.label = new Fields.LabelField(model);
    this.locationCity = new AddressFields.CityField(model, this, {
      propertyName: "locationCity",
    });
    this.locationName = new Fields.LocationNameField(model);
    this.locationPostalCode = new AddressFields.PostalCodeField(model, this, {
      propertyName: "locationPostalCode",
    });
    this.locationState = new AddressFields.StateField(model, this, {
      propertyName: "locationState",
    });
    this.locationStreet = new AddressFields.StreetField(model, this, {
      propertyName: "locationStreet",
    });
    this.locationStreet2 = new AddressFields.Street2Field(model, this, {
      propertyName: "locationStreet2",
    });
    this.isMall = new Fields.IsMallField(model);
    this.isResidence = new Fields.IsResidenceField(model);
    this.mallName = new Fields.MallNameField(model, locationIndex);

    this.fields = [
      this.branchNumber,
      this.label,
      this.locationCity,
      this.locationName,
      this.locationPostalCode,
      this.locationState,
      this.locationStreet,
      this.locationStreet2,
      this.isMall,
      this.isResidence,
      this.mallName,
    ];
  }

  validate({ stringify = true }: { stringify: boolean }): void {
    const Errors = new MerchantValidationError({
      locationsLength: this._model.locations.length,
    });

    let errorCount = 0;
    for (const field of this.fields) {
      try {
        field.validate();
      } catch (ex) {
        errorCount++;
        if (
          Boolean(Errors.fieldErrorMap.locations) &&
          Array.isArray(Errors.fieldErrorMap.locations)
        ) {
          Errors.fieldErrorMap.locations[this.locationIndex][
            field.propertyName as keyof StoreLocationValueMap
          ] = { error: stringify ? ex.message : ex };
        }
      }
    }

    if (errorCount) {
      throw Errors.fieldErrorMap;
    }
  }
}
